import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      diagnosis: 'Diagnosis',
      contact: 'Contact',
      admin: 'Admin',
      login: 'Login',
      logout: 'Logout'
    },
    // Hero Section
    hero: {
      tagline: 'Helping your teams deliver sustainably.',
      headline: 'Build Teams That Engage & Deliver',
      subheadline: 'We align hiring, leadership, and culture to ensure sustainable execution. BELUX expertise for lasting team performance.',
      cta: 'Run Team Performance Diagnosis',
      ctaSecondary: 'Explore Our Services'
    },
    // About
    about: {
      title: 'About TeamCraft',
      vision: 'Our Vision',
      visionText: 'Organisations do not fail because of strategy. They fail because execution breaks down.',
      mission: 'Our Mission',
      missionText: 'To help leaders build teams that <strong>ENGAGE</strong> & <strong>DELIVER</strong> sustainably.',
      approach: 'Our Approach',
      approachText: 'We operate at the intersection of recruitment, leadership development, and organizational transformation.',
      methodology: 'Methodology',
      certifications: 'Certifications'
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Three pillars of sustainable team performance',
      recruitment: {
        title: 'Agile Recruitment',
        problem: 'Recruitment breakdowns cause costly mis-hires and slow value creation.',
        outcomes: 'Clearer role definition, better hiring decisions, faster onboarding, earlier value creation.',
        whatWeDo: 'We audit & upgrade recruitment processes, run alignment interviews, and design onboarding journeys.'
      },
      teamDynamics: {
        title: 'Team Dynamic Enhancement',
        problem: 'Groups fail to perform as teams due to conflict, poor communication, and burnout.',
        outcomes: 'Fewer conflicts, better decisions, stronger accountability, teams that perform without burning out.',
        whatWeDo: 'We run conflict management workshops, team effectiveness sessions, and stress communication training using PCM tools.'
      },
      leadership: {
        title: 'Leadership Support',
        problem: 'Leadership misalignment undermines culture and execution.',
        outcomes: 'Aligned leadership style, reinforced culture, sustainable execution over time.',
        whatWeDo: 'We provide executive coaching, leadership assessment, and organizational transformation guidance.'
      },
      cta: 'Start Your Diagnosis'
    },
    // Diagnosis
    diagnosis: {
      title: 'Team Performance Diagnosis',
      subtitle: 'Discover where your team stands and what to improve',
      step1: 'Organization Profile',
      step2: 'Team Assessment',
      step3: 'Your Results',
      step4: 'Contact Information',
      companySize: 'Company Size',
      industry: 'Industry',
      growthStage: 'Growth Stage',
      teamSize: 'Team Size',
      next: 'Continue',
      back: 'Back',
      submit: 'Get Results',
      questions: {
        hiring_clarity: 'How clear are your role definitions and hiring criteria?',
        leadership_alignment: 'How aligned is your leadership team on vision and culture?',
        team_accountability: 'How strong is accountability within your team?',
        conflict_level: 'How well does your team manage conflicts? (5 = Very well)',
        engagement_level: 'How engaged are your team members?',
        burnout_risk: 'How well is burnout managed in your team? (5 = Very well)'
      },
      scale: {
        poor: 'Poor',
        excellent: 'Excellent'
      }
    },
    // Results
    results: {
      title: 'Your Team Performance Results',
      totalScore: 'Total Score',
      riskLevel: 'Risk Level',
      low: 'Low Risk',
      moderate: 'Moderate Risk',
      high: 'High Risk',
      recommended: 'Recommended Service',
      aiRecommendations: 'Personalized Recommendations',
      bookCall: 'Book a Discovery Call',
      downloadPdf: 'Download PDF Report'
    },
    // Contact
    contact: {
      title: 'Get In Touch',
      name: 'Full Name',
      company: 'Company',
      email: 'Email',
      role: 'Your Role',
      phone: 'Phone (Optional)',
      submit: 'Submit',
      booking: 'Book a Discovery Call',
      selectSlot: 'Select a time slot'
    },
    // Admin
    admin: {
      dashboard: 'Dashboard',
      leads: 'Leads',
      bookings: 'Bookings',
      export: 'Export CSV',
      totalLeads: 'Total Leads',
      newLeads: 'New Leads',
      highRisk: 'High Risk',
      conversion: 'Won Deals',
      filterBy: 'Filter by',
      status: 'Status',
      riskLevel: 'Risk Level',
      tag: 'Tag',
      all: 'All',
      addNote: 'Add Note',
      updateStatus: 'Update Status',
      viewDetails: 'View Details'
    },
    // Footer
    footer: {
      tagline: 'Helping your teams deliver sustainably.',
      location: 'Luxembourg',
      copyright: '© 2026 TeamCraft. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success!',
      learnMore: 'Learn More',
      getStarted: 'Get Started'
    }
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      about: 'À Propos',
      services: 'Services',
      diagnosis: 'Diagnostic',
      contact: 'Contact',
      admin: 'Admin',
      login: 'Connexion',
      logout: 'Déconnexion'
    },
    // Hero Section
    hero: {
      tagline: 'Aider vos équipes à performer durablement.',
      headline: 'Construisez des Équipes qui Engagent & Livrent',
      subheadline: 'Nous alignons recrutement, leadership et culture pour assurer une exécution durable. Expertise luxembourgeoise pour une performance d\'équipe pérenne.',
      cta: 'Lancer le Diagnostic de Performance',
      ctaSecondary: 'Explorer Nos Services'
    },
    // About
    about: {
      title: 'À Propos de TeamCraft',
      vision: 'Notre Vision',
      visionText: 'Les organisations n\'échouent pas à cause de la stratégie. Elles échouent parce que l\'exécution s\'effondre.',
      mission: 'Notre Mission',
      missionText: 'Aider les leaders à construire des équipes qui <strong>S\'ENGAGENT</strong> & <strong>LIVRENT</strong> durablement.',
      approach: 'Notre Approche',
      approachText: 'Nous opérons à l\'intersection du recrutement, du développement du leadership et de la transformation organisationnelle.',
      methodology: 'Méthodologie',
      certifications: 'Certifications'
    },
    // Services
    services: {
      title: 'Nos Services',
      subtitle: 'Trois piliers pour une performance d\'équipe durable',
      recruitment: {
        title: 'Recrutement Agile',
        problem: 'Les échecs de recrutement causent des erreurs d\'embauche coûteuses et ralentissent la création de valeur.',
        outcomes: 'Définition de poste plus claire, meilleures décisions d\'embauche, intégration plus rapide, création de valeur anticipée.',
        whatWeDo: 'Nous auditons et améliorons vos processus de recrutement, menons des entretiens d\'alignement et concevons des parcours d\'intégration.'
      },
      teamDynamics: {
        title: 'Amélioration de la Dynamique d\'Équipe',
        problem: 'Les groupes échouent à performer en équipe à cause des conflits, d\'une mauvaise communication et de l\'épuisement.',
        outcomes: 'Moins de conflits, meilleures décisions, responsabilisation renforcée, équipes qui performent sans s\'épuiser.',
        whatWeDo: 'Nous animons des ateliers de gestion des conflits, des sessions d\'efficacité d\'équipe et des formations en communication sous stress avec les outils PCM.'
      },
      leadership: {
        title: 'Conseil en Leadership',
        problem: 'Le désalignement du leadership mine la culture et l\'exécution.',
        outcomes: 'Style de leadership aligné, culture renforcée, exécution durable dans le temps.',
        whatWeDo: 'Nous proposons du coaching exécutif, de l\'évaluation du leadership et de l\'accompagnement en transformation organisationnelle.'
      },
      cta: 'Commencer Votre Diagnostic'
    },
    // Diagnosis
    diagnosis: {
      title: 'Diagnostic de Performance d\'Équipe',
      subtitle: 'Découvrez où se situe votre équipe et ce qu\'il faut améliorer',
      step1: 'Profil de l\'Organisation',
      step2: 'Évaluation de l\'Équipe',
      step3: 'Vos Résultats',
      step4: 'Informations de Contact',
      companySize: 'Taille de l\'Entreprise',
      industry: 'Secteur d\'Activité',
      growthStage: 'Phase de Croissance',
      teamSize: 'Taille de l\'Équipe',
      next: 'Continuer',
      back: 'Retour',
      submit: 'Obtenir les Résultats',
      questions: {
        hiring_clarity: 'Quelle est la clarté de vos définitions de poste et critères d\'embauche ?',
        leadership_alignment: 'Quel est l\'alignement de votre équipe de direction sur la vision et la culture ?',
        team_accountability: 'Quelle est la force de la responsabilisation au sein de votre équipe ?',
        conflict_level: 'Comment votre équipe gère-t-elle les conflits ? (5 = Très bien)',
        engagement_level: 'Quel est le niveau d\'engagement de vos collaborateurs ?',
        burnout_risk: 'Comment le burnout est-il géré dans votre équipe ? (5 = Très bien)'
      },
      scale: {
        poor: 'Faible',
        excellent: 'Excellent'
      }
    },
    // Results
    results: {
      title: 'Résultats de Performance de Votre Équipe',
      totalScore: 'Score Total',
      riskLevel: 'Niveau de Risque',
      low: 'Risque Faible',
      moderate: 'Risque Modéré',
      high: 'Risque Élevé',
      recommended: 'Service Recommandé',
      aiRecommendations: 'Recommandations Personnalisées',
      bookCall: 'Réserver un Appel Découverte',
      downloadPdf: 'Télécharger le Rapport PDF'
    },
    // Contact
    contact: {
      title: 'Nous Contacter',
      name: 'Nom Complet',
      company: 'Entreprise',
      email: 'Email',
      role: 'Votre Fonction',
      phone: 'Téléphone (Optionnel)',
      submit: 'Envoyer',
      booking: 'Réserver un Appel Découverte',
      selectSlot: 'Sélectionnez un créneau'
    },
    // Admin
    admin: {
      dashboard: 'Tableau de Bord',
      leads: 'Prospects',
      bookings: 'Réservations',
      export: 'Exporter CSV',
      totalLeads: 'Total Prospects',
      newLeads: 'Nouveaux Prospects',
      highRisk: 'Risque Élevé',
      conversion: 'Affaires Gagnées',
      filterBy: 'Filtrer par',
      status: 'Statut',
      riskLevel: 'Niveau de Risque',
      tag: 'Tag',
      all: 'Tous',
      addNote: 'Ajouter une Note',
      updateStatus: 'Mettre à Jour le Statut',
      viewDetails: 'Voir Détails'
    },
    // Footer
    footer: {
      tagline: 'Aider vos équipes à performer durablement.',
      location: 'Luxembourg',
      copyright: '© 2026 TeamCraft. Tous droits réservés.',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation'
    },
    // Common
    common: {
      loading: 'Chargement...',
      error: 'Une erreur s\'est produite',
      success: 'Succès !',
      learnMore: 'En Savoir Plus',
      getStarted: 'Commencer'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
